// Google Analytics 4 integration for React

// Define proper types for Google Analytics
type GtagParams = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer: Array<unknown>;
    gtag: (...args: unknown[]) => void;
  }
}

// Prefer env, but fall back to TreffPunkts GA4 stream and Google Tag ID
export const GA_MEASUREMENT_ID = import.meta.env.VITE_GOOGLE_ANALYTICS_ID || 'G-Q5NBWXYVR1';
export const GOOGLE_TAG_ID = (import.meta as unknown as { env: Record<string, string | undefined> }).env.VITE_GOOGLE_TAG_ID || 'GT-55B9RTXK';

// Determine if GA debug mode should be enabled (shows in GA4 DebugView)
const isGADebugEnabled = (): boolean => {
  try {
    const params = new URLSearchParams(window.location.search);
    // Enable when running locally (vite dev) or URL contains ?ga_debug=1
    return Boolean(import.meta.env.DEV) || params.has('ga_debug');
  } catch {
    return Boolean(import.meta.env.DEV);
  }
};

// DEBUG: Log environment variable availability
console.log('🔍 ENVIRONMENT DEBUG:', {
  GA_MEASUREMENT_ID,
  GOOGLE_TAG_ID,
  allEnvVars: Object.keys(import.meta.env),
  hasGAVar: !!import.meta.env.VITE_GOOGLE_ANALYTICS_ID,
  hasGoogleTagVar: !!(import.meta as unknown as { env: Record<string, string | undefined> }).env.VITE_GOOGLE_TAG_ID,
});

// Initialize Google Analytics
export const initGA = (): void => {
  if (!GA_MEASUREMENT_ID && !GOOGLE_TAG_ID) {
    console.warn('Google Analytics IDs not found');
    return;
  }

  console.log('🔧 DEBUG: Initializing Google Analytics with IDs:', { GA_MEASUREMENT_ID, GOOGLE_TAG_ID });

  // Guard against double-injection if initGA() is ever called twice
  // (e.g. consent toggled on/off/on across a single page load).
  const existingGtagScript = Array.from(document.scripts).some(s => s.src.includes('googletagmanager.com/gtag/js'));
  if (!existingGtagScript) {
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    const loaderId = GA_MEASUREMENT_ID || GOOGLE_TAG_ID;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${loaderId}`;
    gtagScript.onload = () => console.log('✅ DEBUG: gtag script loaded successfully');
    gtagScript.onerror = () => console.error('❌ DEBUG: Failed to load gtag script');
    document.head.appendChild(gtagScript);
    console.log('📡 DEBUG: gtag script added to head:', gtagScript.src);
  } else {
    console.log('ℹ️ DEBUG: gtag script already present');
  }

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    console.log('🔍 DEBUG: gtag called with:', args);
    window.dataLayer.push(args);
  };

  // Configure Google Analytics
  console.log('🔧 DEBUG: Configuring GA4 with domains and settings');
  window.gtag('js', new Date());
  const baseConfig: GtagParams = {
    // Enable tracking across domains
    cookie_domain: 'auto',
    cookie_flags: 'SameSite=None;Secure',
    // Configure for multi-domain tracking
    linker: {
      domains: ['treffpunkts.de', 'treffpunkts.com']
    },
    // We send page_view events explicitly via trackPageView() on route changes,
    // so disable the automatic initial page view from gtag config.
    send_page_view: false,
    allow_google_signals: true,
    allow_ad_personalization_signals: true
  };

  const debugEnabled = isGADebugEnabled();
  if (debugEnabled) {
    baseConfig.debug_mode = true;
    console.log('🧪 GA DebugView enabled (debug_mode=true)');
  }

  if (GA_MEASUREMENT_ID) {
    window.gtag('config', GA_MEASUREMENT_ID, baseConfig);
  }

  console.log('✅ DEBUG: Google Analytics initialized with IDs:', { GA_MEASUREMENT_ID, GOOGLE_TAG_ID });
  console.log('🔍 DEBUG: dataLayer length:', window.dataLayer.length);

  // Optional: log GA client_id for verification in console
  try {
    const anyWindow = window as unknown as { gtag?: (...args: unknown[]) => void };
    if (typeof anyWindow.gtag === 'function' && GA_MEASUREMENT_ID) {
      anyWindow.gtag('get', GA_MEASUREMENT_ID, 'client_id', (clientId: string) => {
        console.log('🆔 GA client_id:', clientId);
      });
    }
  } catch {
    // ignore
  }
};

// Track page views
export const trackPageView = (url: string, title?: string): void => {
  if (typeof window.gtag !== 'undefined' && GA_MEASUREMENT_ID) {
    const debugEnabled = isGADebugEnabled();
    
    // Send as a proper page_view event (recommended for GA4)
    const eventParams: GtagParams = {
      page_title: title,
      page_location: url,
    };
    
    if (debugEnabled) {
      eventParams.debug_mode = true;
    }
    
    console.log('🔍 DEBUG: Sending page_view event to GA4:', { url, title, id: GA_MEASUREMENT_ID, debug: debugEnabled });
    
    // Send page_view event instead of config
    window.gtag('event', 'page_view', eventParams);
    
    console.log('📡 DEBUG: Page view event sent to gtag');
  } else {
    console.error('❌ DEBUG: gtag not available for page view tracking or GA_MEASUREMENT_ID missing');
  }
};

// Track custom events
export const trackEvent = (action: string, category: string, label?: string, value?: number): void => {
  if (typeof window.gtag !== 'undefined') {
    const debugEnabled = isGADebugEnabled();
    const params: GtagParams = {
      event_category: category,
      event_label: label,
      value: value,
    };
    if (debugEnabled) params.debug_mode = true;
    console.log('🔍 DEBUG: Sending event to GA4:', { action, category, label, value, id: GA_MEASUREMENT_ID, debug: debugEnabled });
    window.gtag('event', action, params);
    console.log('📡 DEBUG: Event sent to gtag');
  } else {
    console.error('❌ DEBUG: gtag not available for event tracking');
  }
};

// Track user interactions
export const trackUserAction = (action: string, details?: Record<string, unknown>): void => {
  trackEvent(action, 'user_interaction', details ? JSON.stringify(details) : undefined);
};

// Track activity-related events
export const trackActivityEvent = (action: string, activityId?: number, activityTitle?: string): void => {
  trackEvent(action, 'activity', `${activityId}: ${activityTitle}`);
};

// Track authentication events
export const trackAuthEvent = (action: string, method?: string): void => {
  trackEvent(action, 'authentication', method);
}; 

// GA4 recommended events helpers
export const gaLogin = (method: string): void => {
  if (typeof window.gtag !== 'undefined') {
    const params: GtagParams = { method };
    if (isGADebugEnabled()) params.debug_mode = true;
    window.gtag('event', 'login', params);
  }
};

export const gaSignUp = (method: string): void => {
  if (typeof window.gtag !== 'undefined') {
    const params: GtagParams = { method };
    if (isGADebugEnabled()) params.debug_mode = true;
    window.gtag('event', 'sign_up', params);
  }
};

export const gaSearch = (searchTerm: string, categoryId?: number): void => {
  if (typeof window.gtag !== 'undefined') {
    const params: GtagParams = { search_term: searchTerm };
    if (typeof categoryId === 'number') params.category_id = categoryId;
    if (isGADebugEnabled()) params.debug_mode = true;
    window.gtag('event', 'search', params);
  }
};

export const gaViewPricing = (): void => {
  if (typeof window.gtag !== 'undefined') {
    const params: GtagParams = {};
    if (isGADebugEnabled()) params.debug_mode = true;
    window.gtag('event', 'view_promotion', { ...params, promotion_name: 'pricing_page' });
    window.gtag('event', 'page_view', { ...params, page_title: 'Pricing Plans' });
  }
};

export const gaSelectPlan = (planName: string): void => {
  if (typeof window.gtag !== 'undefined') {
    const params: GtagParams = { plan_name: planName };
    if (isGADebugEnabled()) params.debug_mode = true;
    window.gtag('event', 'select_promotion', { ...params, promotion_name: 'plan_select' });
  }
};

export const gaTrialSignup = (): void => {
  if (typeof window.gtag !== 'undefined') {
    const params: GtagParams = {};
    if (isGADebugEnabled()) params.debug_mode = true;
    window.gtag('event', 'sign_up', { ...params, method: 'free_trial' });
  }
};

export const gaViewActivityDetail = (activityId: number, title?: string): void => {
  if (typeof window.gtag !== 'undefined') {
    const params: GtagParams = {
      items: [
        {
          item_id: String(activityId),
          item_name: title || `Activity ${activityId}`,
          item_category: 'Activity',
        },
      ],
    };
    if (isGADebugEnabled()) params.debug_mode = true;
    window.gtag('event', 'view_item', params);
  }
};

export const gaCreateActivity = (activityId: number, title?: string): void => {
  if (typeof window.gtag !== 'undefined') {
    const params: GtagParams = {
      activity_id: activityId,
      activity_title: title,
    };
    if (isGADebugEnabled()) params.debug_mode = true;
    window.gtag('event', 'create_activity', params);
  }
};

// User identity helpers (avoid PII; Firebase UID is acceptable)
export const gaSetUserId = (userId: string | null): void => {
  if (typeof window.gtag !== 'undefined') {
    try {
      if (userId) {
        window.gtag('config', GA_MEASUREMENT_ID, { user_id: userId });
      } else {
        // Resetting user_id: send config without user_id
        window.gtag('config', GA_MEASUREMENT_ID, {});
      }
    } catch {
      // ignore
    }
  }
};

export const gaSetUserProperties = (props: Record<string, unknown>): void => {
  if (typeof window.gtag !== 'undefined') {
    try {
      window.gtag('set', 'user_properties', props);
    } catch {
      // ignore
    }
  }
};

// Manual testing helper - force send a page view
export const forcePageView = (testTitle = 'Manual Test Page'): void => {
  if (typeof window.gtag !== 'undefined' && GA_MEASUREMENT_ID) {
    console.log('🧪 FORCE TEST: Sending manual page view');
    window.gtag('event', 'page_view', {
      page_title: testTitle,
      page_location: window.location.href,
      debug_mode: true,
      test_event: true
    });
    console.log('✅ FORCE TEST: Manual page view sent');
  }
};

// Test GA4 connection
export const testGA4Connection = (): void => {
  console.log('🔧 GA4 CONNECTION TEST:');
  console.log('- GA_MEASUREMENT_ID:', GA_MEASUREMENT_ID);
  console.log('- gtag available:', typeof window.gtag !== 'undefined');
  console.log('- dataLayer length:', window.dataLayer?.length || 0);
  console.log('- Current URL:', window.location.href);
  
  if (typeof window.gtag !== 'undefined' && GA_MEASUREMENT_ID) {
    console.log('✅ GA4 is properly configured');
    // Send a test event
    window.gtag('event', 'test_connection', {
      event_category: 'debug',
      event_label: 'connection_test',
      debug_mode: true,
      timestamp: new Date().toISOString()
    });
    console.log('📡 Test event sent to GA4');
  } else {
    console.error('❌ GA4 is not properly configured');
  }
};